import { useEffect, useId, useRef, type FormEvent, type ReactNode } from "react";

interface DialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  /** When given, the body is a form and Enter / the primary button submits it. */
  onSubmit?: () => void;
  footer: ReactNode;
  wide?: boolean;
  children: ReactNode;
}

/**
 * Native <dialog>: the browser handles focus trapping, Escape and the backdrop, and screen
 * readers announce it as a modal.
 */
export function Dialog({ open, title, onClose, onSubmit, footer, wide = false, children }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
    }
    if (!open && dialog.open) {
      if (typeof dialog.close === "function") dialog.close();
      else dialog.removeAttribute("open");
    }
  }, [open]);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    onSubmit?.();
  }

  const content = (
    <>
      <header className="dialog-header">
        <h2 id={titleId}>{title}</h2>
      </header>
      <div className="dialog-body">{children}</div>
      <footer className="dialog-footer">{footer}</footer>
    </>
  );

  return (
    <dialog
      ref={ref}
      className={wide ? "dialog dialog-wide" : "dialog"}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      {open ? (
        onSubmit ? (
          <form className="dialog-form" onSubmit={handleSubmit} noValidate>
            {content}
          </form>
        ) : (
          <div className="dialog-form">{content}</div>
        )
      ) : null}
    </dialog>
  );
}
